import type { ICellRendererParams } from "../ui/table"
import type { CSSProperties, ReactNode } from "react"
import { useState } from "react"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TOOLTIP_HINT_CLASS,
} from "../ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import StatusBadge from "./StatusBadge"
import type { ActiveGridFilter } from "../../utils/gridColumnFilters"
import { copyToClipboard } from "../../utils/clipboard"
import { ENTITY_STATUS_STYLE } from "../../utils/status"
import { cn } from "../../lib/utils"
import {
  BsArrowRepeat,
  BsCheckLg,
  BsPencil,
  BsThreeDotsVertical,
  BsTrash,
} from "react-icons/bs"
import { TbCopy } from "react-icons/tb"

export const GRID_PAGE_SIZE = 50
export const GRID_PAGE_SIZE_SELECTOR = [50, 100, 200, 500]
export const GRID_LIST_FETCH_SIZE = 1000

export type ChipTone = { bg: string; color: string }

export const GRID_CHIP_TONE = {
  neutral: ENTITY_STATUS_STYLE.unknown,
  category: { bg: "rgba(148,163,184,0.14)", color: "#94a3b8" },
  currency: { bg: "rgba(91,127,255,0.15)", color: "#5B7FFF" },
  code: { bg: "rgba(91,127,255,0.12)", color: "#5B7FFF" },
  terminal: { bg: "rgba(38,166,154,0.12)", color: "#26a69a" },
} as const

export const PAYMENT_METHOD_CHIP_TONE: Record<string, ChipTone> = {
  visa: { bg: "rgba(91,127,255,0.15)", color: "#5B7FFF" },
  mastercard: { bg: "rgba(255,165,2,0.15)", color: "#ffa502" },
  sepa: { bg: "rgba(46,213,115,0.15)", color: "#2ed573" },
  amex: { bg: "rgba(167,139,250,0.15)", color: "#a78bfa" },
  "apple pay": { bg: "rgba(255,71,87,0.15)", color: "#ff4757" },
  "google pay": { bg: "rgba(38,166,154,0.15)", color: "#26a69a" },
}

export function GridChip({
  label,
  tone = GRID_CHIP_TONE.neutral,
  className,
  mono = false,
  active = false,
  nativeTitle = true,
}: {
  label: string
  tone?: ChipTone
  className?: string
  mono?: boolean
  active?: boolean
  /** Native browser tooltip. Turn off when a shared Tooltip wraps this chip. */
  nativeTitle?: boolean
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "max-w-full min-w-0 shrink-0 border-0 px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        mono && "font-mono tracking-wide",
        active && "ring-2 ring-primary ring-offset-1 ring-offset-background",
        className
      )}
      style={{ background: tone.bg, color: tone.color }}
      title={nativeTitle ? label : undefined}
    >
      <span className="truncate">{label}</span>
    </Badge>
  )
}

export function EmptyGridCell() {
  return <span className="text-muted-foreground">—</span>
}

export function GridCellWrap({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("flex h-full w-full min-w-0 items-center gap-1 overflow-hidden py-1 flex-nowrap", className)}>
      {children}
    </div>
  )
}

export const COPY_ON_HOVER_CELL_CLASS = "copy-on-hover-cell"
export const COPY_ON_HOVER_BTN_CLASS = "copy-on-hover-btn"

export const GRID_HINT_TOOLTIP_CLASS = TOOLTIP_HINT_CLASS

export function CopyableGridText({
  text,
  copyValue,
  className,
  textClassName,
  copyMessage = "Copied to clipboard",
  copyLabel = "Copy",
  hint,
}: {
  text: string
  copyValue?: string
  className?: string
  textClassName?: string
  copyMessage?: string
  copyLabel?: string
  /** Full value shown in the shared tooltip when the cell text is cut off. */
  hint?: string
}) {
  const [copied, setCopied] = useState(false)
  const valueToCopy = copyValue ?? text

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation()
    const ok = await copyToClipboard(valueToCopy, copyMessage)
    if (!ok) return
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <span
      className={cn(
        COPY_ON_HOVER_CELL_CLASS,
        "inline-flex min-w-0 w-full max-w-full items-center gap-1.5",
        className
      )}
    >
      {hint ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn("min-w-0 truncate", textClassName)}>{text}</span>
          </TooltipTrigger>
          <TooltipContent side="top" align="start" className={GRID_HINT_TOOLTIP_CLASS}>
            {hint}
          </TooltipContent>
        </Tooltip>
      ) : (
        <span className={cn("min-w-0 truncate", textClassName)}>{text}</span>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              COPY_ON_HOVER_BTN_CLASS,
              "h-6 w-6 shrink-0",
              copied && "copy-on-hover-btn-visible text-emerald-500 hover:text-emerald-500"
            )}
            onClick={handleCopy}
            aria-label={copyLabel}
          >
            {copied ? <BsCheckLg className="h-3.5 w-3.5" /> : <TbCopy className="h-3.5 w-3.5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{copied ? "Copied" : copyLabel}</TooltipContent>
      </Tooltip>
    </span>
  )
}

export function CategoryChipCellRenderer<T>(params: ICellRendererParams<T, string>) {
  const value = params.value
  if (!value || value === "—") {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    )
  }
  return (
    <GridCellWrap>
      <GridChip label={String(value)} tone={GRID_CHIP_TONE.category} />
    </GridCellWrap>
  )
}

export function CodeChipCellRenderer<T>(params: ICellRendererParams<T, string>) {
  const value = params.value
  if (!value) {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    )
  }
  return (
    <GridCellWrap>
      <GridChip label={String(value)} tone={GRID_CHIP_TONE.code} mono />
    </GridCellWrap>
  )
}

export function TerminalChipCellRenderer<T>(params: ICellRendererParams<T, string>) {
  const value = params.value
  if (!value || value === "—") {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    )
  }
  return (
    <GridCellWrap>
      <GridChip label={String(value)} tone={GRID_CHIP_TONE.terminal} />
    </GridCellWrap>
  )
}

export function CurrencyChipCellRenderer<T>(params: ICellRendererParams<T, string>) {
  const value = params.value
  if (!value || value === "—") {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    )
  }
  return (
    <GridCellWrap>
      <GridChip label={String(value)} tone={GRID_CHIP_TONE.currency} />
    </GridCellWrap>
  )
}

export type ChipListCellProps = {
  items: string[]
  title: string
  limit?: number
  isCurrency?: boolean
  tone?: ChipTone
  toneForItem?: (item: string) => ChipTone | undefined
  onShowMore?: (title: string, items: string[], isCurrency?: boolean) => void
  onFilterClick?: (value: string) => void
  activeFilter?: string | string[] | null
  clickable?: boolean
}

export function ChipListCell({
  items,
  title,
  limit = 2,
  isCurrency = false,
  tone,
  toneForItem,
  onShowMore,
  onFilterClick,
  activeFilter = null,
  clickable = true,
}: ChipListCellProps) {
  if (!items.length) {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    )
  }

  const visible = items.slice(0, limit)
  const hidden = items.length - visible.length
  const defaultTone = isCurrency ? GRID_CHIP_TONE.currency : (tone ?? GRID_CHIP_TONE.neutral)
  const useSplitActions = Boolean(onFilterClick)

  const handleShowMore = (e: React.MouseEvent) => {
    if (!clickable || !onShowMore) return
    e.stopPropagation()
    onShowMore(title, items, isCurrency)
  }

  const handleFilterClick = (e: React.MouseEvent, value: string) => {
    if (!clickable || !onFilterClick) return
    e.stopPropagation()
    onFilterClick(value)
  }

  const handleLegacyClick = (e: React.MouseEvent) => {
    if (!clickable || !onShowMore) return
    e.stopPropagation()
    onShowMore(title, items, isCurrency)
  }

  const isFilterMatch = (value: string) => {
    if (activeFilter == null) return false
    const normalized = value.trim().toLowerCase()
    if (Array.isArray(activeFilter)) {
      return activeFilter.some(
        (filterValue) => filterValue.trim().toLowerCase() === normalized,
      )
    }
    return normalized === activeFilter.trim().toLowerCase()
  }

  return (
    <div
      className={cn(
        "flex h-full flex-wrap items-center gap-1 py-1",
        !useSplitActions && clickable && onShowMore && "cursor-pointer"
      )}
      onClick={useSplitActions ? undefined : handleLegacyClick}
      role={!useSplitActions && clickable && onShowMore ? "button" : undefined}
      tabIndex={!useSplitActions && clickable && onShowMore ? 0 : undefined}
    >
      {visible.map((item) => (
        <span
          key={item}
          className={cn(useSplitActions && onFilterClick && "cursor-pointer")}
          onClick={
            useSplitActions
              ? (e) => handleFilterClick(e, item)
              : undefined
          }
          role={useSplitActions && onFilterClick ? "button" : undefined}
          tabIndex={useSplitActions && onFilterClick ? 0 : undefined}
        >
          <GridChip
            label={item}
            tone={toneForItem?.(item) ?? defaultTone}
            active={isFilterMatch(item)}
          />
        </span>
      ))}
      {hidden > 0 && (
        <Badge
          variant="outline"
          className={cn(
            "shrink-0 border border-dashed border-muted-foreground/30 bg-transparent px-2 py-0.5 text-xs font-medium text-muted-foreground",
            clickable && onShowMore && "cursor-pointer hover:border-primary/50 hover:text-primary"
          )}
          onClick={handleShowMore}
          role={clickable && onShowMore ? "button" : undefined}
          tabIndex={clickable && onShowMore ? 0 : undefined}
        >
          +{hidden}
        </Badge>
      )}
    </div>
  )
}

export type ChipSummaryCellProps = {
  items: string[]
  title: string
  isCurrency?: boolean
  tone?: ChipTone
  toneForItem?: (item: string) => ChipTone | undefined
  onShowMore?: (title: string, items: string[], isCurrency?: boolean) => void
  onFilterClick?: (value: string) => void
  activeFilter?: string | string[] | null
}

/** Shows the first item plus a +N badge; chip click filters, +N opens the full list dialog. */
export function ChipSummaryCell({
  items,
  title,
  isCurrency = false,
  tone,
  toneForItem,
  onShowMore,
  onFilterClick,
  activeFilter,
}: ChipSummaryCellProps) {
  return (
    <ChipListCell
      items={items}
      title={title}
      limit={1}
      isCurrency={isCurrency}
      tone={tone}
      toneForItem={toneForItem}
      onShowMore={onShowMore}
      onFilterClick={onFilterClick}
      activeFilter={activeFilter}
    />
  )
}

export function ActiveGridFiltersBar({
  filters,
  onRemove,
  onClearAll,
}: {
  filters: ActiveGridFilter[]
  onRemove: (key: string) => void
  onClearAll: () => void
}) {
  if (!filters.length) return null

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border/60 px-4 py-2.5">
      <span className="text-xs font-medium text-muted-foreground">Active filters:</span>
      {filters.map((filter) => (
        <Badge
          key={filter.key}
          variant="secondary"
          className="gap-1.5 pr-1.5 text-xs font-medium"
        >
          <span>
            {filter.label}: {filter.value}
          </span>
          <button
            type="button"
            className="rounded-sm px-0.5 text-sm leading-none text-muted-foreground hover:text-foreground"
            onClick={() => onRemove(filter.key)}
            aria-label={`Remove ${filter.label} filter`}
          >
            ×
          </button>
        </Badge>
      ))}
      <button
        type="button"
        className="text-xs font-medium text-primary hover:underline"
        onClick={onClearAll}
      >
        Clear all
      </button>
    </div>
  )
}

export type NameCellParams = {
  name: string
  accent: string
  muted?: boolean
}

/** Light circular initials — set `--avatar-accent` on `.acq-avatar` (see compact-layout.css). */
export function accentAvatarStyle(accent: string): CSSProperties {
  return { '--avatar-accent': accent } as CSSProperties
}

/** Single initial for entity grid avatars (Merchants, Acquirers, User management, etc.). */
export function nameAvatarInitials(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return "?"
  return trimmed.charAt(0).toUpperCase()
}

export function NameAvatarCell({ name, accent, muted = false }: NameCellParams) {
  const initials = nameAvatarInitials(name)

  return (
    <GridCellWrap>
      <div className="acq-name-cell">
        <div className="acq-avatar" style={accentAvatarStyle(accent)}>
          {initials}
        </div>
        <div className={cn("acq-name truncate", muted && "text-muted-foreground")}>{name}</div>
      </div>
    </GridCellWrap>
  )
}

export function SubtitleCellRenderer<T>(params: ICellRendererParams<T, string>) {
  const value = params.value
  if (!value) return <GridCellWrap><EmptyGridCell /></GridCellWrap>
  return (
    <GridCellWrap>
      <span className="text-muted-foreground">{value}</span>
    </GridCellWrap>
  )
}

export function CopyableEmailText({ email }: { email: string }) {
  return (
    <CopyableGridText
      text={email}
      textClassName="text-muted-foreground"
      copyMessage="Email copied to clipboard"
      copyLabel="Copy email"
    />
  )
}

export function EmailCopyCellRenderer<T>(params: ICellRendererParams<T, string>) {
  const value = params.value?.trim()
  if (!value || value === "—") {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    )
  }

  return (
    <GridCellWrap>
      <CopyableEmailText email={value} />
    </GridCellWrap>
  )
}

export function StatusBadgeCellRenderer<T>(params: ICellRendererParams<T, string>) {
  return (
    <GridCellWrap>
      <StatusBadge status={params.value} />
    </GridCellWrap>
  )
}

export type EntityActionHandlers<T> = {
  onStatus?: (row: T) => void
  onEdit?: (row: T) => void
  onDelete?: (row: T) => void
  statusLabel?: string
  editLabel?: string
  deleteLabel?: string
}

function ActionsMenuItemButton({
  icon,
  label,
  onClick,
  destructive = false,
}: {
  icon: ReactNode
  label: string
  onClick: () => void
  destructive?: boolean
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted",
        destructive ? "text-destructive hover:bg-destructive/10" : "text-foreground",
      )}
      onClick={onClick}
    >
      <span className="text-base">{icon}</span>
      {label}
    </button>
  )
}

export function EntityActionsCellRenderer<T extends { id?: number }>(
  params: ICellRendererParams<T>
) {
  const row = params.data
  const handlers = params.context?.actions as EntityActionHandlers<T> | undefined
  const [menuOpen, setMenuOpen] = useState(false)
  if (!row || !handlers) return null

  return (
    <div className="flex h-full items-center py-1" onClick={(e) => e.stopPropagation()}>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="tm-compact-icon-btn shrink-0"
            aria-label="Actions"
          >
            <BsThreeDotsVertical />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          {handlers.onStatus && (
            <ActionsMenuItemButton
              icon={<BsArrowRepeat />}
              label={handlers.statusLabel ?? "Change status"}
              onClick={() => {
                setMenuOpen(false)
                handlers.onStatus?.(row)
              }}
            />
          )}
          {handlers.onEdit && (
            <ActionsMenuItemButton
              icon={<BsPencil />}
              label={handlers.editLabel ?? "Edit"}
              onClick={() => {
                setMenuOpen(false)
                handlers.onEdit?.(row)
              }}
            />
          )}
          {handlers.onDelete && (
            <ActionsMenuItemButton
              icon={<BsTrash />}
              label={handlers.deleteLabel ?? "Delete"}
              destructive
              onClick={() => {
                setMenuOpen(false)
                handlers.onDelete?.(row)
              }}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}

export function EditDeleteActionsCellRenderer<T>(
  params: ICellRendererParams<T>
) {
  const row = params.data
  const handlers = params.context?.actions as {
    onEdit?: (row: T) => void
    onDelete?: (row: T) => void
  } | undefined
  const [menuOpen, setMenuOpen] = useState(false)
  if (!row || !handlers) return null

  return (
    <div className="flex h-full items-center py-1" onClick={(e) => e.stopPropagation()}>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="tm-compact-icon-btn shrink-0"
            aria-label="Actions"
          >
            <BsThreeDotsVertical />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          {handlers.onEdit && (
            <ActionsMenuItemButton
              icon={<BsPencil />}
              label="Edit"
              onClick={() => {
                setMenuOpen(false)
                handlers.onEdit?.(row)
              }}
            />
          )}
          {handlers.onDelete && (
            <ActionsMenuItemButton
              icon={<BsTrash />}
              label="Delete"
              destructive
              onClick={() => {
                setMenuOpen(false)
                handlers.onDelete?.(row)
              }}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
